import { apiBaseUrl, getAuthToken } from './client'

export interface UploadProgress {
  loaded: number
  total: number
}

/** A failed upload: the HTTP status (0 for a network error or an abort), the
 * parsed error body when there is one, and the request id the API stamped. */
export class UploadError extends Error {
  status: number
  body: unknown
  requestId?: string

  constructor(status: number, body: unknown, requestId?: string) {
    const title =
      body && typeof body === 'object' && typeof (body as { title?: unknown }).title === 'string'
        ? (body as { title: string }).title
        : undefined
    super(title ?? (status === 0 ? 'Network error' : `HTTP ${status}`))
    this.name = 'UploadError'
    this.status = status
    this.body = body
    this.requestId = requestId
  }
}

export interface UploadWithProgressOptions {
  url: string
  /** A multipart body. Either this or `body`. */
  formData?: FormData
  /** A raw body (a chunk of a chunked upload). Either this or `formData`. */
  body?: Blob
  /** Defaults to POST. */
  method?: string
  headers?: Record<string, string>
  onProgress?: (progress: UploadProgress) => void
  signal?: AbortSignal
}

/**
 * Sends a FormData (POST by default) or a raw Blob with XMLHttpRequest, which
 * (unlike fetch) reports upload progress. Sends the bearer token like the openapi-fetch client does. Resolves
 * with the parsed JSON body, rejects with an UploadError.
 */
export function uploadWithProgress<T = unknown>(options: UploadWithProgressOptions): Promise<T> {
  const { url, formData, body, method, headers, onProgress, signal } = options
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method ?? 'POST', url)
    const token = getAuthToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    for (const [name, value] of Object.entries(headers ?? {})) xhr.setRequestHeader(name, value)

    if (onProgress) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) onProgress({ loaded: event.loaded, total: event.total })
      }
    }

    const parse = (): unknown => {
      if (!xhr.responseText) return undefined
      try {
        return JSON.parse(xhr.responseText)
      } catch {
        return undefined
      }
    }

    xhr.onload = () => {
      const body = parse()
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T)
      } else {
        reject(new UploadError(xhr.status, body, xhr.getResponseHeader('X-Request-ID') ?? undefined))
      }
    }
    xhr.onerror = () => reject(new UploadError(0, undefined))
    xhr.ontimeout = () => reject(new UploadError(0, undefined))
    xhr.onabort = () => reject(new UploadError(0, undefined))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })

    xhr.send(body ?? formData ?? null)
  })
}

export function documentsUploadUrl(org: string): string {
  return `${apiBaseUrl}/api/v1/organizations/${encodeURIComponent(org)}/documents`
}
